import { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthToken } from '../../../utils/authStorage';
import './SolarReportFields.css';

export const emptySolarReport = { includePending:false, inspector:'', irradianceWm2:'', ambientC:'', windMs:'', cloudCover:'', operatingState:'', observations:'', recommendations:'' };
export function solarReportOptions(values, imageIds, findingsOnly) {
  const result={...values,imageIds,findingsOnly};
  for(const key of ['irradianceWm2','ambientC','windMs']) result[key]=values[key]===''?null:Number(values[key]);
  for(const [key,min,max] of [['irradianceWm2',0,2000],['ambientC',-80,80],['windMs',0,100]]) {
    const value=result[key];
    if(value!==null && (!Number.isFinite(value)||value<min||value>max)) throw new Error(`Check ${key}: allowed range ${min} to ${max}.`);
  }
  return result;
}

export default function SolarReportFields({ inspectionId, imageId, values, onChange, disabled }) {
  const [metadata,setMetadata]=useState(null),[metadataError,setMetadataError]=useState('');
  useEffect(()=>{
    setMetadata(null);setMetadataError('');if(!imageId)return;
    const controller=new AbortController();
    axios.get(`/api/inspections/${inspectionId}/solar-report/metadata`,{params:{imageId},signal:controller.signal,headers:{Authorization:`Bearer ${getAuthToken()}`}})
      .then(({data})=>{if(!controller.signal.aborted)setMetadata(data);})
      .catch(()=>{if(!controller.signal.aborted)setMetadataError('Metadata preview unavailable. The report will read available metadata from the originals.');});
    return()=>controller.abort();
  },[inspectionId,imageId]);
  const field=(key,label,type='text',extra={})=><label key={key}>{label}<input type={type} value={values[key]} maxLength={200} {...extra} onChange={event=>onChange({...values,[key]:event.target.value})}/></label>;
  return <fieldset className="solar-report-fields" disabled={disabled}>
    <legend>Thermal report</legend>
    <label className="solar-report-check"><input type="checkbox" checked={values.includePending} onChange={event=>onChange({...values,includePending:event.target.checked})}/> Preliminary report: include pending candidates</label>
    <p>{values.includePending?'Pending candidates will be clearly marked as unreviewed.':'Only confirmed findings are included. Deleted anomalies are always excluded.'}</p>
    <div className="solar-report-metadata" role="status">
      {metadata ? <><strong>From image metadata</strong><span>Camera: {metadata.camera}</span><span>Captured: {metadata.capturedAt}</span><small>Source: {metadata.sourceImage}. Each report image retains its own metadata.</small></> : metadataError || 'Reading image metadata…'}
    </div>
    <h4>Inspection conditions (optional)</h4>
    <p>Enter field measurements only. These values are saved in the PDF; they do not change the thermal calculations.</p>
    <div className="solar-report-grid">
      {field('inspector','Inspector')}
      {field('irradianceWm2','Irradiance (W/m²)','number',{min:0,max:2000,step:'any'})}
      {field('ambientC','Ambient temperature (°C)','number',{min:-80,max:80,step:'any'})}
      {field('windMs','Wind speed (m/s)','number',{min:0,max:100,step:'any'})}
      {field('cloudCover','Cloud cover')}
      {field('operatingState','Plant operating state')}
    </div>
    <label>Inspector observations<textarea rows={3} maxLength={4000} value={values.observations} onChange={event=>onChange({...values,observations:event.target.value})}/></label>
    <label>Recommended actions<textarea rows={3} maxLength={4000} value={values.recommendations} onChange={event=>onChange({...values,recommendations:event.target.value})}/></label>
  </fieldset>;
}
